import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clusterApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';
import type { Site } from '@/types';
import { siteClusterInstalled } from '../shared';

// ---- Edit Site Dialog ----
//
// Surfaces every Site field that the backend's PATCH /sites/:id accepts so
// post-creation edits don't require DB surgery. The MetalLB pool fields are
// the immediate motivator — sites created before the columns landed have
// them NULL, which keeps the cluster's MetalLB controller running with no
// IPAddressPool installed, which keeps every LoadBalancer Service stuck at
// externalIP=<pending>, which hides the LAN-access and NMOS-Registry URL
// rows in the Site Overview. Filling the pools in here triggers the
// deployment-service prereq reconciler to apply the IPAddressPool +
// L2Advertisement manifests on its next pass; LoadBalancers come up with
// real IPs and the UI rows reappear.
//
// Fields that are intentionally NOT in this form:
//   - tier            — changing the resource profile of a running site has
//                       wide blast radius; needs its own confirm flow.
//   - talosVersion /  — these track what the cluster has actually installed;
//     kubernetesVer.    operator should drive these via a planned upgrade,
//                       not a free-form edit.
//   - lockdown         — already has a dedicated toggle elsewhere in the
//                       Overview.
//   - config (JSONB)   — opaque blob, no validation here.

interface EditSiteDialogProps {
  site: Site;
  onClose: () => void;
  onSave: (patch: Partial<Site>) => void;
  isSaving: boolean;
  /**
   * The failed save, shown as the server's message: its `message`, else its
   * `error`. It showed the raw `API error 400: {…}` (JSS#959).
   */
  saveError: unknown;
}

export function EditSiteDialog({ site, onClose, onSave, isSaving, saveError }: EditSiteDialogProps) {
  const [name, setName] = useState(site.name ?? '');
  const [description, setDescription] = useState(site.description ?? '');
  const [location, setLocation] = useState(site.location ?? '');
  const [proxyAddress, setProxyAddress] = useState(site.proxyAddress ?? '');
  const [metallbIngestPool, setMetallbIngestPool] = useState(site.metallbIngestPool ?? '');
  const [metallbGeneralPool, setMetallbGeneralPool] = useState(site.metallbGeneralPool ?? '');
  // The two-pool split is a JBS broadcast edge case (separate L2 segment for
  // SMPTE-2110 ingest). Hide it behind a disclosure so the general operator
  // only sees one input. If the site already has an ingest pool configured,
  // pop the section open so the value is visible — otherwise the user would
  // think it had been erased.
  const [showIngestPool, setShowIngestPool] = useState(
    (site.metallbIngestPool ?? '').trim() !== '',
  );

  // Pull live node IPs from the cluster so operators don't have to keep a
  // hand-typed proxyAddress in sync with whatever Talos/DHCP actually gave
  // each node. Gated on an installed site (active or degraded) for the same reason
  // clusterApi.getServices is on the site detail page — pre-bootstrap the
  // K8s API isn't reachable.
  const {
    data: clusterNodes,
    isLoading: nodesLoading,
    error: nodesError,
  } = useQuery({
    queryKey: ['cluster-nodes', site.id],
    queryFn: () => clusterApi.getNodes(site.id),
    enabled: !!site.id && siteClusterInstalled(site.status),
    staleTime: 30_000,
  });

  // List of (hostname, InternalIP) pairs we'll render. Falls back to the
  // node's `name` (Kubernetes node name) when no Hostname address is set —
  // Talos typically populates both, but be defensive.
  const nodeIpRows = (clusterNodes ?? [])
    .map((n) => {
      const internalIp =
        n.addresses.find((a) => a.type === 'InternalIP')?.address ??
        n.managementIp ??
        null;
      const hostname =
        n.addresses.find((a) => a.type === 'Hostname')?.address ?? n.name;
      return { hostname, ip: internalIp };
    })
    .filter((r): r is { hostname: string; ip: string } => r.ip !== null);

  // Default the override disclosure to expanded when the existing
  // proxyAddress doesn't match any node IP — the operator presumably set it
  // by hand for a reason and we don't want to silently hide that.
  const proxyMatchesNode =
    site.proxyAddress != null &&
    site.proxyAddress !== '' &&
    nodeIpRows.some((r) => r.ip === site.proxyAddress);
  const initialOverrideOpen =
    (site.proxyAddress ?? '') !== '' && !proxyMatchesNode;
  const [showProxyOverride, setShowProxyOverride] = useState(initialOverrideOpen);

  // Once the node list loads we may discover the existing proxyAddress is
  // in fact a node IP — collapse the override panel in that case so the UI
  // matches steady state.
  useEffect(() => {
    if (nodeIpRows.length > 0 && proxyMatchesNode) {
      setShowProxyOverride(false);
    }
  }, [proxyMatchesNode, nodeIpRows.length]);

  // Lightweight range syntax check: accepts `<ip>-<ip>` or CIDR `<ip>/<n>`,
  // matches the MetalLB IPAddressPool addresses field shape. Empty is fine
  // (means "clear the pool"). Doesn't validate that the addresses are real
  // IPv4 / non-overlapping across sites — the cluster will reject those on
  // apply.
  const looksLikePoolValue = (value: string): boolean => {
    if (value.trim() === '') return true;
    const v = value.trim();
    if (/^\d{1,3}(?:\.\d{1,3}){3}-\d{1,3}(?:\.\d{1,3}){3}$/.test(v)) return true;
    if (/^\d{1,3}(?:\.\d{1,3}){3}\/\d{1,2}$/.test(v)) return true;
    return false;
  };

  const ingestPoolOk = looksLikePoolValue(metallbIngestPool);
  const generalPoolOk = looksLikePoolValue(metallbGeneralPool);
  const nameOk = name.trim() !== '';
  const formOk = nameOk && ingestPoolOk && generalPoolOk;

  // Build the patch with only fields that actually changed — backend uses
  // COALESCE so omitted fields stay put, but submitting an empty patch is
  // legal and we'd rather not write an updated_at touch for a no-op.
  const buildPatch = (): Partial<Site> => {
    const patch: Partial<Site> = {};
    const nameTrimmed = name.trim();
    if (nameTrimmed !== (site.name ?? '')) patch.name = nameTrimmed;
    const descTrimmed = description.trim();
    if (descTrimmed !== (site.description ?? '')) patch.description = descTrimmed;
    const locTrimmed = location.trim();
    if (locTrimmed !== (site.location ?? '')) patch.location = locTrimmed;
    const proxyTrimmed = proxyAddress.trim();
    if (proxyTrimmed !== (site.proxyAddress ?? '')) patch.proxyAddress = proxyTrimmed;
    const ingestTrimmed = metallbIngestPool.trim();
    if (ingestTrimmed !== (site.metallbIngestPool ?? '')) {
      (patch as Partial<Site> & { metallbIngestPool?: string }).metallbIngestPool = ingestTrimmed;
    }
    const generalTrimmed = metallbGeneralPool.trim();
    if (generalTrimmed !== (site.metallbGeneralPool ?? '')) {
      (patch as Partial<Site> & { metallbGeneralPool?: string }).metallbGeneralPool = generalTrimmed;
    }
    return patch;
  };

  const handleSave = () => {
    if (!formOk) return;
    onSave(buildPatch());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-xl mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-silver-bright">Edit Site</h3>
          <button onClick={onClose} className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">
              Name <span className="text-status-error">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 resize-y"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Location</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="block text-xs font-medium text-silver-muted">
                Node IPs <span className="text-silver-muted/60">(from cluster)</span>
              </label>
            </div>
            {!siteClusterInstalled(site.status) ? (
              <p className="text-xs text-silver-muted/70 italic">
                Node IPs unavailable until cluster is active.
              </p>
            ) : nodesLoading ? (
              <p className="text-xs text-silver-muted/70 italic">Loading node IPs…</p>
            ) : nodesError ? (
              <p className="text-xs text-status-warning">
                Couldn't reach the cluster API — fall back to manual override below.
              </p>
            ) : nodeIpRows.length === 0 ? (
              <p className="text-xs text-silver-muted/70 italic">
                Cluster reported no nodes with InternalIP set.
              </p>
            ) : (
              <div className="rounded-lg border border-surface-elevated bg-surface-deep px-3 py-2 space-y-1">
                {nodeIpRows.map((row) => (
                  <div
                    key={`${row.hostname}-${row.ip}`}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-silver-muted font-mono">{row.hostname}</span>
                    <span className="text-silver-bright font-mono">{row.ip}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowProxyOverride((v) => !v)}
              className="mt-2 flex items-center gap-1 text-xs text-silver-muted hover:text-silver-bright transition-colors"
            >
              {showProxyOverride ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Advanced — override proxy address
            </button>
            {showProxyOverride && (
              <div className="mt-2">
                <input
                  value={proxyAddress}
                  onChange={(e) => setProxyAddress(e.target.value)}
                  className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
                  placeholder="10.0.10.21"
                />
                <p className="text-xs text-silver-muted/70 mt-1">
                  Manual fallback used when the cluster API isn't reachable (early bootstrap). Leave blank to rely on the live node IPs above.
                </p>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-surface-elevated">
            <label className="block text-xs font-medium text-silver-muted mb-1.5">MetalLB IP Range</label>
            <input
              value={metallbGeneralPool}
              onChange={(e) => setMetallbGeneralPool(e.target.value)}
              className={cn(
                'w-full bg-surface-deep border rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none',
                generalPoolOk
                  ? 'border-surface-elevated focus:border-gold/50'
                  : 'border-status-error/60 focus:border-status-error',
              )}
              placeholder="10.0.10.150-10.0.10.199"
            />
            {!generalPoolOk ? (
              <p className="text-xs text-status-error mt-1">
                Use <span className="font-mono">ip-ip</span> or <span className="font-mono">ip/cidr</span>
              </p>
            ) : (
              <p className="text-xs text-silver-muted/80 mt-1.5">
                IP range (<span className="font-mono">10.0.10.150-10.0.10.199</span>) or CIDR (<span className="font-mono">10.0.10.150/27</span>) handed out to LoadBalancer Services. Leave empty to install MetalLB without a pool — Services will stay at <span className="font-mono">externalIP=&lt;pending&gt;</span>. Must not overlap other sites on the same L2 segment.
              </p>
            )}

            <button
              type="button"
              onClick={() => setShowIngestPool((v) => !v)}
              className="mt-3 flex items-center gap-1 text-xs text-silver-muted hover:text-silver-bright transition-colors"
            >
              {showIngestPool ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Advanced — broadcast ingest pool
            </button>

            {showIngestPool && (
              <div className="mt-2 pl-4 border-l border-surface-elevated">
                <label className="block text-xs font-medium text-silver-muted mb-1.5">Ingest Pool</label>
                <input
                  value={metallbIngestPool}
                  onChange={(e) => setMetallbIngestPool(e.target.value)}
                  className={cn(
                    'w-full bg-surface-deep border rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder-silver-muted/50 focus:outline-none',
                    ingestPoolOk
                      ? 'border-surface-elevated focus:border-gold/50'
                      : 'border-status-error/60 focus:border-status-error',
                  )}
                  placeholder="10.0.10.100-10.0.10.149"
                />
                {!ingestPoolOk ? (
                  <p className="text-xs text-status-error mt-1">
                    Use <span className="font-mono">ip-ip</span> or <span className="font-mono">ip/cidr</span>
                  </p>
                ) : (
                  <p className="text-xs text-silver-muted/80 mt-1.5">
                    Optional. SMPTE-2110 broadcast ingest traffic. Leave empty unless you&apos;re running JBS with a dedicated L2 segment for video ingest.
                  </p>
                )}
              </div>
            )}
          </div>

          {saveError != null && (
            <p role="alert" className="text-xs text-status-error">
              {parseApiError(saveError).message}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formOk || isSaving}
            className="px-4 py-2 bg-gold text-black text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
